/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { usePlayerCurrentMedia } from "..";
import { usePlayerCallbacks } from "../../../shared/context/PlayerCallbacksContext";
import {
  AssetPlaybackStatus,
  MediaStreamType,
  PlayerError,
} from "../../../shared/types";
import { usePlayerController } from "../../BasePlayer";

import { useContinueWatchingConfiguration } from "./useContinueWatchingConfiguration";
import { useStartPositionLoader } from "./useStartPositionLoader";

export const useContinueWatchingMark = () => {
  const { player } = usePlayerController();
  const { onProgressUpdate } = usePlayerCallbacks();

  const { syncIntervalSeconds } = useContinueWatchingConfiguration();

  const {
    media: { Id: mediaId },
    streamType,
    playInfo,
  } = usePlayerCurrentMedia();

  const { startPosition } = useStartPositionLoader({
    playInfo,
    streamType,
    enabled: true,
  });

  const [_, setCastDeviceName] = useState<string>(player.getCastDeviceName());

  const lastTimeRef = useRef<number>(0);
  const isTrialStream = streamType === MediaStreamType.Trial;

  const stopCastProgress = useCallback(async () => {
    if (isTrialStream || !onProgressUpdate) {
      return;
    }

    const currentTime = Math.floor(player.currentTime());

    try {
      await onProgressUpdate({
        mediaId,
        progressInSeconds: currentTime,
        status: AssetPlaybackStatus.Stopped,
      });
    } catch (error) {
      if (__DEV__) {
        console.error(error);
      }
    }
  }, [isTrialStream, mediaId, onProgressUpdate, player]);

  const sendMarker = useCallback(
    (timeInSeconds: number, status?: AssetPlaybackStatus) => {
      if (!onProgressUpdate) {
        return;
      }

      lastTimeRef.current = timeInSeconds;
      onProgressUpdate({
        mediaId,
        progressInSeconds: timeInSeconds,
        status,
      }).catch((error: PlayerError) => {
        console.error("Unable to save watch progress", error);

        if (error?.MessageKey === "ASSET_CONCURRENT_PLAYBACK_LIMIT") {
          player.setError(error.Message as string, error.MessageKey);
          player.pause();
        }
      });
    },
    [mediaId, onProgressUpdate, player],
  );

  const timeHandler = useCallback(() => {
    if (player.isCastConnected()) {
      return;
    }

    const currentPlayerTime = Math.floor(player.currentTime());

    const shouldSendMarker = currentPlayerTime % syncIntervalSeconds === 0;

    if (shouldSendMarker && lastTimeRef.current !== currentPlayerTime) {
      sendMarker(currentPlayerTime, AssetPlaybackStatus.Streaming);
    }
  }, [player, sendMarker, syncIntervalSeconds]);

  const pausePlayHandler = useCallback(() => {
    const currentPlayerTime = Math.floor(player.currentTime());

    const status = player.isPaused()
      ? AssetPlaybackStatus.Paused
      : AssetPlaybackStatus.Streaming;

    if (player.isCastConnected()) {
      return;
    }

    sendMarker(currentPlayerTime, status);
  }, [player, sendMarker]);

  const endedHandler = useCallback(() => {
    if (player.isCastConnected()) {
      return;
    }

    const currentPlayerTime = Math.floor(player.currentTime());

    sendMarker(currentPlayerTime, AssetPlaybackStatus.Ended);
  }, [player, sendMarker]);

  const disposeHandler = useCallback(() => {
    if (player.isCastConnected()) {
      stopCastProgress();
      return;
    }

    const currentPlayerTime = Math.floor(player.currentTime());

    sendMarker(
      currentPlayerTime !== startPosition &&
        currentPlayerTime === 0 &&
        startPosition > 0
        ? startPosition
        : currentPlayerTime,
      AssetPlaybackStatus.Stopped,
    );
  }, [player, sendMarker, startPosition, stopCastProgress]);

  useEffect(() => {
    if (isTrialStream) {
      return;
    }

    const unsubscribeTimeUpdate = player.onEvent("timeupdate", timeHandler);
    const unsubscribePausePlay = player.onEvent(
      ["pause", "playing"],
      pausePlayHandler,
    );
    const unsubscribeEnded = player.onEvent("ended", endedHandler);
    const unsubscribeDispose = player.onEvent("dispose", disposeHandler);

    return () => {
      unsubscribeTimeUpdate();
      unsubscribePausePlay();
      unsubscribeEnded();
      unsubscribeDispose();
    };
  }, [
    disposeHandler,
    endedHandler,
    isTrialStream,
    pausePlayHandler,
    player,
    timeHandler,
  ]);

  useEffect(() => {
    const unsubscribeCastConnected = player.onEvent(
      ["castconnected"],
      async () => {
        setCastDeviceName(player.getCastDeviceName());

        if (!isTrialStream && onProgressUpdate) {
          const currentPlayerTime = Math.floor(player.currentTime());
          try {
            await onProgressUpdate({
              mediaId,
              progressInSeconds: currentPlayerTime,
              status: AssetPlaybackStatus.Paused,
            });
          } catch (error) {
            if (__DEV__) {
              console.error(error);
            }
          }
        }
      },
    );

    const unsubscribeCastDisconnected = player.onEvent(
      ["castdisconnected"],
      async () => {
        await stopCastProgress();
      },
    );

    return () => {
      unsubscribeCastConnected();
      unsubscribeCastDisconnected();
    };
  }, [isTrialStream, mediaId, onProgressUpdate, player, stopCastProgress]);
};
